import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

type Variant = 'primary' | 'secondary' | 'ghost'
type Tone = 'light' | 'dark'
type Size = 'sm' | 'md'

interface ButtonLinkProps {
    href: string
    children: ReactNode
    variant?: Variant
    tone?: Tone
    size?: Size
    className?: string
    newTab?: boolean
    disabledLabel?: ReactNode
    ariaLabel?: string
}

const base = 'group/btn inline-flex items-center justify-center gap-2 rounded-full font-medium whitespace-nowrap select-none transition-[color,background-color,border-color,box-shadow,translate] duration-[250ms] ease-out-soft [&_svg]:transition-transform [&_svg]:duration-[250ms] [&_svg]:ease-out-soft'

const motion = 'hover:-translate-y-0.5 active:translate-y-0 active:duration-100 motion-reduce:hover:translate-y-0'

const sizes: Record<Size, string> = {
    sm: 'h-10 px-4.5 text-[13.5px]',
    md: 'h-12 px-6 text-[15px]'
}

const variants: Record<Variant, Record<Tone, string>> = {
    primary: {
        light: 'bg-chocolate text-ivory shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_12px_24px_-16px_rgba(53,28,26,0.8)] hover:bg-burgundy hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_18px_32px_-16px_rgba(53,28,26,0.85)]',
        dark: 'bg-ivory text-chocolate shadow-[0_12px_28px_-16px_rgba(0,0,0,0.7)] hover:bg-cream'
    },
    secondary: {
        light: 'border border-ink/15 text-ink hover:border-ink/40 hover:bg-ink/[0.03]',
        dark: 'border border-ivory/25 text-ivory hover:border-ivory/50 hover:bg-ivory/[0.05]'
    },
    ghost: {
        light: 'text-ink-soft hover:text-ink',
        dark: 'text-ivory/70 hover:text-ivory'
    }
}

const disabledStyles: Record<Tone, string> = {
    light: 'cursor-not-allowed border border-dashed border-ink/20 bg-sand/70 text-ink-soft',
    dark: 'cursor-not-allowed border border-dashed border-ivory/25 bg-ivory/[0.06] text-ivory/65'
}

export const buttonClasses = (variant: Variant = 'primary', tone: Tone = 'light', size: Size = 'md', className?: string): string => {
    return cn(base, motion, sizes[size], variants[variant][tone], className)
}

export const ButtonLink: FC<ButtonLinkProps> = ({
    href,
    children,
    variant = 'primary',
    tone = 'light',
    size = 'md',
    className,
    newTab,
    disabledLabel,
    ariaLabel
}): ReactNode => {
    if (!href) {
        return (
            <span
                role='link'
                aria-disabled='true'
                className={cn(base, sizes[size], disabledStyles[tone], className)}
            >
                {disabledLabel ?? children}
            </span>
        )
    }

    const classes = buttonClasses(variant, tone, size, className)
    const isInternal = href.startsWith('/') || href.startsWith('#')

    if (isInternal) {
        return (
            <Link href={href} className={classes} aria-label={ariaLabel}>
                {children}
            </Link>
        )
    }

    const openInNewTab = newTab ?? true

    return (
        <a
            href={href}
            className={classes}
            aria-label={ariaLabel}
            target={openInNewTab ? '_blank' : undefined}
            rel={openInNewTab ? 'noopener noreferrer' : undefined}
        >
            {children}
        </a>
    )
}
